import * as React from 'react';
import { Paper, makeStyles, Typography, TextField, Hidden, Theme } from '@material-ui/core';
import CustomButton from '../../shared/Button';
import CardLogo from '../../shared/CardLogo';
import ChipIcon from '../../shared/ChipIcon';
import { splitCardNumber } from './helpers';
import { DatePicker } from '@material-ui/pickers';
import { useStore } from '../../store/StoreProvider';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '60px 44px',
    borderRadius: '10px',
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      zIndex: 10,
      top: 0,
      left: 0,
      transform: 'none',
      padding: '70px 10px',
      width: '100%',
      height: '100vh',
      boxSizing: 'border-box'
    }
  },
  partsWrapper: {
    display: 'flex',
    padding: '40px 0',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse'
    }
  },
  left: {
    flex: '1 1 0',
    marginRight: '100px',
    [theme.breakpoints.down('xs')]: {
      marginRight: 0
    }
  },
  card: {
    width: '347px',
    height: '182px',
    padding: '18px 28px',
    boxSizing: 'border-box',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      marginBottom: '24px',
      margin: 'auto'
    }
  },
  cardRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  field: {
    width: '355px',
    marginBottom: '24px',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  fieldMargin: {
    marginRight: '35px'
  },
  button: {
    width: '350px',
    fontSize: '21px'
  },
  cardNumber: {
    fontSize: '22px',
    lineHeight: '1.2'
  },
  masterCard: {
    width: '38px',
    height: '28px',
    display: 'flex',
    '&:before, &:after': {
      content: '\'\'',
      width: '28px',
      height: '28px',
      backgroundColor: '#f1f1f1',
      borderRadius: '50%',
      mixBlendMode: 'multiply',
      flexShrink: '0'
    },
    '&:after': {
      marginLeft: '-10px'
    }
  },
  title: {
    fontWeight: 700,
  }
}));

type ProfileFormData = {
  cardName: string;
  cardNumber: string;
  expiryDate: Date;
  cvc: string;
};

const Profile = () => {
  const cl = useStyles();
  const { card, isCardSaved, setIsCardSaved, addCard } = useStore();
  const [ formData, setFormData ] = React.useState<ProfileFormData>({
    cardName: card ? card.cardName : '',
    cardNumber: card ? card.cardNumber : '',
    expiryDate: card ? new Date(card.expiryDate) : new Date(),
    cvc: card ? card.cvc : '' 
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let v = value;

    if (name === 'cardNumber') {
      v = splitCardNumber(value);
    }

    setFormData({
      ...formData,
      [name]: v
    });
  };
  const handleSubmit = () => {
    addCard(formData);
  }

  React.useEffect(() => {
    setIsCardSaved(false);
  }, []);

  return (
    <div style={{ pointerEvents: 'all' }}>
      <Hidden only='xs'>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: '#1C1A19', opacity: 0.5 }}/>
      </Hidden>
      <Paper className={cl.paper} >
        <Typography className={cl.title} variant='h4' align='center'>Профиль</Typography>
        {
          isCardSaved ?
          <>
            <Typography align='center' style={{ marginTop: '16px' }}>Платёжные данные обновлены. Теперь вы можете заказывать такси.</Typography>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
              <CustomButton component={Link} to='/main/order' classname={cl.button}>Перейти на карту</CustomButton>
            </div>
          </> :
          <>
            <Typography align='center'>Введите платежные данные</Typography>
            <div className={cl.partsWrapper}>
              <div className={cl.left}>
                <form>
                  <TextField
                    className={cl.field}
                    name='cardName'
                    label='Имя владельца'
                    value={formData.cardName}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                  <TextField
                    className={cl.field}
                    name='cardNumber'
                    label='Номер карты'
                    value={formData.cardNumber}
                    onChange={handleChange}
                    inputProps={{ maxLength: 19 }}
                    required
                    fullWidth
                  />
                  <div style={{ display: 'flex' }}>
                    <DatePicker
                      className={cl.fieldMargin}
                      name='expiryDate'
                      openTo="year"
                      views={["year", "month"]}
                      format='MM/yy'
                      label="MM/YY"
                      minDate={new Date()}
                      maxDate={new Date("2028-12-31")}
                      value={formData.expiryDate}
                      required
                      onChange={value => {
                        setFormData({ ...formData, 'expiryDate': value });
                      }}
                    />
                    <TextField
                      name='cvc'
                      label='CVC'
                      value={formData.cvc}
                      required
                      onChange={handleChange}
                      inputProps={{ maxLength: 3 }}
                    />
                  </div>
                </form>
              </div>
              <div style={{ flex: '1 1 0' }}>
                <Paper className={cl.card} elevation={5}>
                  <div className={cl.cardRow}>
                    <CardLogo />
                    <Typography>{formData.expiryDate ? `${formData.expiryDate.getMonth().toString().length === 1 ? 0 : ''}${formData.expiryDate.getMonth() + 1}/${formData.expiryDate.getFullYear().toString().slice(-2)}` : '00/00'}</Typography>
                  </div>
                  <div>
                    <Typography className={cl.cardNumber}>{formData.cardNumber || '0000 0000 0000 0000'}</Typography>
                  </div>
                  <div className={cl.cardRow}>
                    <ChipIcon />
                    <div className={cl.masterCard} />
                  </div>
                </Paper>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CustomButton onClick={handleSubmit} classname={cl.button}>Сохранить</CustomButton>
            </div>
          </>
        }
      </Paper>
    </div>
  );
}

export default observer(Profile);
